import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import {
  COMPOSER_DRAFTS_CHANGED_EVENT,
  countComposerDrafts,
  fetchComposerDrafts,
} from '../utils/composerDrafts';

const UnreadInboxContext = createContext(null);

const EMPTY_COUNTS = { inbox: 0, unread: 0, important: 0, sent: 0, drafts: 0, spam: 0, trash: 0 };

export const useUnreadInbox = () => {
  const context = useContext(UnreadInboxContext);
  if (!context) {
    throw new Error('useUnreadInbox must be used within an UnreadInboxProvider');
  }
  return context;
};

export const UnreadInboxProvider = ({ children }) => {
  const { isAuthenticated, user, loading: authLoading, token } = useAuth();
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [mailboxQuota, setMailboxQuota] = useState(null);
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const mailboxKey = String(user?.email || '').trim().toLowerCase();

  const refreshFolderCounts = useCallback(async () => {
    if (!isAuthenticated || authLoading || !token) {
      if (!isAuthenticated) {
        setCounts(EMPTY_COUNTS);
        setMailboxQuota(null);
      }
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res = await axios.get('/api/emails/folder-counts', {
        timeout: 45000,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!mountedRef.current) return;
      if (res.data?.success) {
        setCounts((prev) => {
          const pick = (key) => {
            const raw = res.data[key];
            if (raw == null || raw === '') return prev[key] || 0;
            const n = Number(raw);
            return Number.isFinite(n) ? Math.max(0, n) : prev[key] || 0;
          };
          return {
            inbox: pick('inbox'),
            unread: pick('unread'),
            important: pick('important'),
            sent: pick('sent'),
            drafts: countComposerDrafts(mailboxKey),
            spam: pick('spam'),
            trash: pick('trash'),
          };
        });
        if (res.data.unreachable) {
          /* Quota unverändert lassen */
        } else if (res.data.quota?.supported) {
          setMailboxQuota(res.data.quota);
        } else if (res.data.configured === false) {
          setMailboxQuota(null);
        } else if (Object.prototype.hasOwnProperty.call(res.data, 'quota')) {
          // Server kennt Quota, Mailserver liefert keine → ausblenden
          setMailboxQuota(null);
        }
      }
    } catch (err) {
      // Bei 401 nicht erneut anfragen — sonst doppelter Fehler + Logout-Race
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        return;
      }
      // Fallback: nur Ungelesen (ältere Server ohne folder-counts)
      try {
        const res = await axios.get('/api/emails/unread-count', {
          timeout: 15000,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!mountedRef.current) return;
        if (res.data?.success) {
          setCounts((prev) => ({
            ...prev,
            unread: Number(res.data.count) || 0,
          }));
        }
      } catch (__) {
        /* IMAP ggf. nicht konfiguriert */
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [isAuthenticated, authLoading, token, mailboxKey]);

  const refreshUnreadCount = useCallback(async () => {
    await refreshFolderCounts();
  }, [refreshFolderCounts]);

  const decrementUnread = useCallback((by = 1) => {
    setCounts((prev) => ({
      ...prev,
      unread: Math.max(0, prev.unread - by),
    }));
  }, []);

  const adjustFolderCount = useCallback((key, delta) => {
    if (!key || !delta) return;
    setCounts((prev) => ({
      ...prev,
      [key]: Math.max(0, (Number(prev[key]) || 0) + delta),
    }));
  }, []);

  const setFolderCount = useCallback((key, value) => {
    if (!key) return;
    setCounts((prev) => ({
      ...prev,
      [key]: Math.max(0, Number(value) || 0),
    }));
  }, []);

  const setFolderCountAtLeast = useCallback((key, value) => {
    if (!key) return;
    const n = Math.max(0, Number(value) || 0);
    setCounts((prev) => ({
      ...prev,
      [key]: Math.max(Number(prev[key]) || 0, n),
    }));
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (!isAuthenticated || authLoading) {
      if (!isAuthenticated) {
        setCounts(EMPTY_COUNTS);
        setMailboxQuota(null);
      }
      return undefined;
    }
    setCounts({
      ...EMPTY_COUNTS,
      drafts: countComposerDrafts(mailboxKey),
    });
    setMailboxQuota(null);
    fetchComposerDrafts(mailboxKey)
      .then(() => {
        if (!mountedRef.current) return;
        setCounts((prev) => ({
          ...prev,
          drafts: countComposerDrafts(mailboxKey),
        }));
      })
      .catch(() => {});
    const start = window.setTimeout(refreshFolderCounts, 1200);
    const interval = setInterval(refreshFolderCounts, 30_000);
    let focusTimer = null;
    const onFocus = () => {
      window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(refreshFolderCounts, 400);
    };
    const syncDrafts = () => {
      setCounts((prev) => ({
        ...prev,
        drafts: countComposerDrafts(mailboxKey),
      }));
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener(COMPOSER_DRAFTS_CHANGED_EVENT, syncDrafts);
    return () => {
      mountedRef.current = false;
      window.clearTimeout(start);
      clearInterval(interval);
      window.clearTimeout(focusTimer);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(COMPOSER_DRAFTS_CHANGED_EVENT, syncDrafts);
    };
  }, [isAuthenticated, authLoading, mailboxKey, refreshFolderCounts]);

  const value = useMemo(
    () => ({
      unreadCount: counts.unread,
      folderCounts: counts,
      mailboxQuota,
      setUnreadCount: (n) =>
        setCounts((prev) => ({
          ...prev,
          unread: typeof n === 'function' ? n(prev.unread) : Number(n) || 0,
        })),
      refreshUnreadCount,
      refreshFolderCounts,
      decrementUnread,
      adjustFolderCount,
      setFolderCount,
      setFolderCountAtLeast,
    }),
    [counts, mailboxQuota, refreshUnreadCount, refreshFolderCounts, decrementUnread, adjustFolderCount, setFolderCount, setFolderCountAtLeast]
  );

  return (
    <UnreadInboxContext.Provider value={value}>
      {children}
    </UnreadInboxContext.Provider>
  );
};
